import type {
  getCareerProfile,
  searchCareerConnections,
  searchCareerConnectionsSchema,
} from "../worker/career";
import type {
  listRelationships,
  relationshipContext,
  linkRelationshipSchema,
  upsertConnectionSchema,
} from "../worker/relationships";
import type { listInvitations } from "../worker/invitations";
import type { z } from "zod";
import type {
  Activity,
  BoardChangesResponse,
  BoardPage,
  BoardResponse,
  CardDetail,
  CardKind,
  CompanyJobsLinkResponse,
  GlobalTasksResponse,
  LinkedInCardResponse,
  Stage,
  Task,
} from "../shared/contracts";

class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: init?.body
      ? { "Content-Type": "application/json", ...init.headers }
      : init?.headers,
  });
  if (response.status === 204) return undefined as T;
  const body = (await response.json().catch(() => null)) as {
    error?: { message?: string };
  } | null;
  if (!response.ok)
    throw new ApiRequestError(
      response.status,
      body?.error?.message ?? "Request failed",
    );
  return body as T;
}

export const api = {
  invitations: (connectionId: string) =>
    request<Awaited<ReturnType<typeof listInvitations>>>(
      `/api/relationships/${encodeURIComponent(connectionId)}/invitations`,
    ),
  decideInvitation: (
    id: string,
    input: {
      operationKey: string;
      decision: "approved" | "declined";
      binding: Awaited<
        ReturnType<typeof listInvitations>
      >["drafts"][number]["binding"];
    },
  ) =>
    request(
      `/api/relationships/invitations/${encodeURIComponent(id)}/decision`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    ),
  careerProfile: (connectionId: string, reviewCursor = "") =>
    request<Awaited<ReturnType<typeof getCareerProfile>>>(
      `/api/relationships/${encodeURIComponent(connectionId)}/career${reviewCursor ? `?reviewCursor=${encodeURIComponent(reviewCursor)}` : ""}`,
    ),
  careerConnections: (
    input: z.input<typeof searchCareerConnectionsSchema> = {},
  ) => {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(input))
      if (value !== undefined && value !== "") params.set(name, String(value));
    return request<Awaited<ReturnType<typeof searchCareerConnections>>>(
      `/api/relationships/career-search?${params}`,
    );
  },
  relationshipsForCard: (cardId: string) =>
    request<{ connections: { id: string; name: string }[] }>(
      `/api/relationships/for-card/${encodeURIComponent(cardId)}`,
    ),
  relationships: (query = "", cursor = "") =>
    request<Awaited<ReturnType<typeof listRelationships>>>(
      `/api/relationships?q=${encodeURIComponent(query)}&cursor=${encodeURIComponent(cursor)}`,
    ),
  relationship: (id: string) =>
    request<Awaited<ReturnType<typeof relationshipContext>>>(
      `/api/relationships/${encodeURIComponent(id)}`,
    ),
  linkRelationship: (input: z.input<typeof linkRelationshipSchema>) =>
    request<Awaited<ReturnType<typeof relationshipContext>>>(
      "/api/relationships/link",
      { method: "POST", body: JSON.stringify(input) },
    ),
  upsertConnection: (input: z.input<typeof upsertConnectionSchema>) =>
    request<{
      connection?: { id: string };
      ambiguous: boolean;
      candidates?: { id: string; name?: string }[];
    }>("/api/relationships/connections", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  decideDraft: (
    id: string,
    input: {
      operationKey: string;
      contentHash: string;
      decision: "approved" | "declined";
    },
  ) =>
    request(`/api/relationships/drafts/${encodeURIComponent(id)}/decision`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  resolveSuggestion: (id: string, key: string) =>
    request(
      `/api/relationships/suggestions/${encodeURIComponent(id)}/resolve`,
      { method: "POST", body: JSON.stringify({ operationKey: key }) },
    ),
  session: () =>
    request<{ authenticated: boolean; email?: string; expiresAt?: string }>(
      "/api/auth/session",
    ),
  requestCode: (email: string) =>
    request<{ accepted: true }>("/api/auth/request-code", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verifyCode: (email: string, code: string) =>
    request<{ authenticated: true }>("/api/auth/verify", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),
  logout: () =>
    request<{ authenticated: false }>("/api/auth/logout", {
      method: "POST",
      body: "{}",
    }),
  board: () => request<BoardResponse>("/api/board"),
  stagePage: (stage: Stage, cursor?: string | null) =>
    request<BoardPage>(
      `/api/board/stages/${stage}?limit=40${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  boardChanges: (after: string) =>
    request<BoardChangesResponse>(
      `/api/board/changes?after=${encodeURIComponent(after)}`,
    ),
  search: (query: string, cursor?: string | null) =>
    request<BoardPage>(
      `/api/board/search?q=${encodeURIComponent(query)}&limit=40${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  card: (id: string) => request<CardDetail>(`/api/cards/${id}`),
  summarizeCard: (id: string) =>
    request<CardDetail>(`/api/cards/${id}/summary`, {
      method: "POST",
      body: "{}",
    }),
  createCard: (input: {
    kind: CardKind;
    primaryName: string;
    company?: string;
    sourceUrl?: string;
    contactEmail?: string | null;
  }) =>
    request<CardDetail>("/api/cards", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createCardFromLink: (url: string) =>
    request<LinkedInCardResponse>("/api/cards/from-link", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),
  refreshLinkedIn: (id: string) =>
    request<Pick<LinkedInCardResponse, "card" | "ingestion">>(
      `/api/cards/${id}/refresh-linkedin`,
      { method: "POST", body: "{}" },
    ),
  companyJobsLink: (id: string) =>
    request<CompanyJobsLinkResponse>(`/api/cards/${id}/company-jobs-link`, {
      method: "POST",
      body: "{}",
    }),
  updateCard: (
    id: string,
    input: Partial<
      Pick<
        CardDetail,
        | "kind"
        | "primaryName"
        | "company"
        | "description"
        | "sourceUrl"
        | "contactEmail"
      >
    >,
  ) =>
    request<CardDetail>(`/api/cards/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  moveCard: (
    id: string,
    input: {
      stage: Stage;
      beforeId?: string;
      afterId?: string;
      placement?: "start" | "end";
    },
  ) =>
    request<CardDetail>(`/api/cards/${id}/move`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createTask: (
    cardId: string,
    input: { title: string; dueOn?: string | null },
  ) =>
    request<Task>(`/api/cards/${cardId}/tasks`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateTask: (
    id: string,
    input: { title?: string; dueOn?: string | null; completed?: boolean },
  ) =>
    request<Task>(`/api/tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  deleteTask: (id: string) =>
    request<void>(`/api/tasks/${id}`, { method: "DELETE", body: "{}" }),
  tasks: (status: "open" | "completed") =>
    request<GlobalTasksResponse>(`/api/tasks?status=${status}`),
  addComment: (cardId: string, body: string) =>
    request<Activity>(`/api/cards/${cardId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
};

export { ApiRequestError };
